import { useFinanceIqEnabled } from '@/enterprise/data/useFinanceIqEnabled';
import useFeature from '@/lib/features';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import FinanceIqFallbackAlert from './FinanceIqFallbackAlert';
import KpiSpotlightBannerContent from './KpiSpotlightBannerContent';
import KpiSpotlightBannerSkeleton from './KpiSpotlightBannerSkeleton';
import LegacyFinanceIqAlert from './LegacyFinanceIqAlert';

export interface KpiSpotlightBannerProps {
  makerOrganizationUuid: string;
  currency: string;
  buyerName: string;
}

/**
 * Self-contained Invoice Central banner: owns its own feature gates, loading skeleton, and error
 * fallback so nothing it does can affect the rest of the page.
 *
 * Two independent gates:
 * - `useFinanceIqEnabled()` — account eligibility for FinanceIQ at all. Nothing renders when this
 *   is false, matching the original static banner's behavior.
 * - `enterprise-ui_enableKpiSpotlightBanner` — rollout kill switch for the personalized banner
 *   itself. OFF renders the untouched original static promo (`LegacyFinanceIqAlert`) with no call
 *   to the spotlight endpoint, so the new banner can be rolled back independently of FinanceIQ
 *   eligibility if something in it (or its shared shell) turns out to be wrong.
 */
const KpiSpotlightBanner = ({ makerOrganizationUuid, currency, buyerName }: KpiSpotlightBannerProps) => {
  const isFinanceIqEnabled = useFinanceIqEnabled();
  const [isSpotlightBannerEnabled] = useFeature('enterprise-ui_enableKpiSpotlightBanner');

  if (!isFinanceIqEnabled) {
    return null;
  }

  if (!isSpotlightBannerEnabled) {
    return <LegacyFinanceIqAlert />;
  }

  return (
    // Stable single wrapper around the boundary. When an already-mounted Suspense boundary
    // re-suspends (switching buyer/currency changes the spotlight query key), React keeps the old
    // banner in the DOM with an inline 'display: none' and renders the fallback beside it. The
    // parent's Tailwind `space-y-*` selector matches on the `hidden` attribute, not computed
    // display, so that still-present node counted as a sibling and pushed a phantom 1.5rem
    // margin-top onto the skeleton — a gap the resolved banner never has. Keeping both nodes
    // inside this wrapper keeps the banner's child index in the parent fixed at one.
    <div>
      <QueryBoundaries LoadingComponent={KpiSpotlightBannerSkeleton} ErrorComponent={FinanceIqFallbackAlert}>
        <KpiSpotlightBannerContent
          makerOrganizationUuid={makerOrganizationUuid}
          currency={currency}
          buyerName={buyerName}
        />
      </QueryBoundaries>
    </div>
  );
};

export default KpiSpotlightBanner;
